import React, { useState, useEffect, useContext } from "react";
import {
  Users,
  Heart,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
  FileText,
  ArrowUp,
  ArrowDown,
  IndianRupee,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import { Link } from "react-router-dom";
import { FaRupeeSign } from "react-icons/fa";
import DoctorReportGenerator from "./DoctorReportGenerator"; // Import the report component

const DoctorDashboard = () => {
  const {
    loginUser,
    getAppointments,
    allAppointments,
    allAppointmentsLoading,
  } = useContext(AuthContext);

  // User data
  const userData = {
    name: loginUser?.name || "Pet Parent",
    email: loginUser?.email || "user@email.com",
    phone: "+1 (555) 123-4567",
    address: "123 Pet Lover Lane, Animal City, AC 12345",
  };

  const [selectedTimeRange, setSelectedTimeRange] = useState("today");
  const [dashboardStats, setDashboardStats] = useState({
    totalPets: 0,
    totalAppointments: 0,
    totalUsers: 0,
    pendingAppointments: 0,
    completedAppointments: 0,
    cancelledAppointments: 0,
    revenue: 0,
    revenueGrowth: 0,
  });

  const [recentAppointments, setRecentAppointments] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false); // State for report modal

  // Fetch data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch recent appointments for this doctor
      await getAppointments({
        doctor: loginUser.id,
        limit: 100, // Get more data for comprehensive report
        page: 1,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  // Calculate dashboard statistics and extract recent appointments
  useEffect(() => {
    if (allAppointments) {
      let appointments = [];

      // Handle the API response structure
      if (allAppointments.success && allAppointments.data) {
        appointments = allAppointments.data;
      } else if (Array.isArray(allAppointments)) {
        appointments = allAppointments;
      }

      if (appointments.length > 0) {
        const pendingAppointments = appointments.filter(
          (apt) => apt.status === "pending"
        ).length;
        const completedAppointments = appointments.filter(
          (apt) => apt.status === "completed"
        ).length;
        const cancelledAppointments = appointments.filter(
          (apt) => apt.status === "cancelled"
        ).length;

        setDashboardStats({
          totalAppointments: allAppointments?.total || appointments.length,
          totalUsers: 156, // Mock data - replace with actual user count
          pendingAppointments,
          completedAppointments,
          cancelledAppointments,
          revenue: 12500, // Mock data
          revenueGrowth: 8.5, // Mock data
        });

        // Set recent appointments (limit to 5 most recent)
        const sortedAppointments = appointments
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);

        setRecentAppointments(sortedAppointments);
      }
    }
  }, [allAppointments]);

  const getAppointmentStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getOwnerName = (item) => {
    // Debug: Log the item structure to console
    console.log("Item structure:", item);
    console.log("Pet data:", item.pets || item);
    console.log(
      "Owner data:",
      item.pets?.ownerId || item.ownerId || item.owner
    );

    // Try different possible data structures
    const ownerData =
      item.pets?.ownerId || item.ownerId || item.pets?.owner || item.owner;

    if (!ownerData) {
      console.log("No owner data found");
      return "Unknown Owner";
    }

    // Try userDetails first (most common structure)
    if (ownerData.userDetails) {
      const userDetails = ownerData.userDetails;
      if (userDetails.name) return userDetails.name;
      if (userDetails.firstName) {
        return (
          userDetails.firstName +
          (userDetails.lastName ? " " + userDetails.lastName : "")
        );
      }
      if (userDetails.email) return userDetails.email;
    }

    // Try direct owner properties
    if (ownerData.name) return ownerData.name;
    if (ownerData.firstName) {
      return (
        ownerData.firstName +
        (ownerData.lastName ? " " + ownerData.lastName : "")
      );
    }
    if (ownerData.email) return ownerData.email;

    console.log("No valid owner name found in:", ownerData);
    return "Unknown Owner";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleGenerateReport = () => {
    setShowReportModal(true);
  };

  const StatCard = ({ title, value, icon: Icon, color, growth, loading }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? (
              <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
            ) : (
              value
            )}
          </p>
          {growth !== undefined && (
            <p className="text-sm text-gray-500 mt-1 flex items-center">
              {growth > 0 ? (
                <ArrowUp className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <ArrowDown className="w-4 h-4 text-red-500 mr-1" />
              )}
              {Math.abs(growth)}% from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (allAppointmentsLoading && recentAppointments.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading Doctor dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Doctor Dashboard
              </h1>
              <p className="text-gray-600 mt-1">
                {console.log("User properties:", {
                  id: loginUser.id,
                  name: loginUser.name,
                  email: loginUser.email,
                  role: loginUser.role,
                })}
                Welcome back, {userData.name}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatCard
            title="Total Appointments"
            value={dashboardStats.totalAppointments}
            icon={Calendar}
            color="bg-blue-500"
            // growth={8.2}
            loading={allAppointmentsLoading}
          />
          <StatCard
            title="Pending Appointments"
            value={dashboardStats.pendingAppointments}
            icon={Clock}
            color="bg-yellow-500"
            loading={allAppointmentsLoading}
          />
          <StatCard
            title="Completed Today"
            value={dashboardStats.completedAppointments}
            icon={CheckCircle}
            color="bg-green-500"
            loading={allAppointmentsLoading}
          />
          <StatCard
            title="Monthly Revenue"
            value={`₹${dashboardStats.revenue.toLocaleString()}`}
            icon={IndianRupee}
            color="bg-emerald-500"
            // growth={dashboardStats.revenueGrowth}
            loading={false}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Appointments */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Recent Appointments
                </h2>
                <Link
                  to="/dashboard/all-appointment"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View All 
                </Link>
              </div>

              {allAppointmentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">
                    Loading appointments...
                  </span>
                </div>
              ) : recentAppointments.length > 0 ? (
                <div className="space-y-4">
                  {recentAppointments.map((appointment) => (
                    <div
                      key={appointment._id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {appointment.pets?.name?.charAt(0) || "P"}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {appointment.pets?.name || "Unknown Pet"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {appointment.pets?.ownerId?.userDetails?.name ||
                              appointment.pets?.owner?.name ||
                              "Unknown Owner"}
                          </div>
                          <div className="text-xs text-gray-400">
                            {appointment.type} • {formatDate(appointment.date)}{" "}
                            at {formatTime(appointment.time)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAppointmentStatusColor(
                            appointment.status
                          )}`}
                        >
                          {appointment.status.charAt(0).toUpperCase() +
                            appointment.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-sm">
                    No recent appointments
                  </p>
                  <p className="text-gray-400 text-xs">
                    Your recent appointments will appear here
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Quick Actions
              </h2>
              <div className="space-y-3">
                <Link
                  to="/dashboard/all-appointment"
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  <span>View All Appointments</span>
                </Link>
                <button
                  onClick={handleGenerateReport}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  <span>Generate Report</span>
                </button>
              </div>
            </div>

            {/* Today's Schedule Preview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Today's Schedule
              </h2>
              {recentAppointments.filter((apt) => {
                const today = new Date().toISOString().split("T")[0];
                return apt.date === today;
              }).length > 0 ? (
                <div className="space-y-3">
                  {recentAppointments
                    .filter((apt) => {
                      const today = new Date().toISOString().split("T")[0];
                      return apt.date === today;
                    })
                    .slice(0, 3)
                    .map((appointment) => (
                      <div
                        key={appointment._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatTime(appointment.time)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {appointment.pets?.name} - {appointment.type}
                          </div>
                        </div>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAppointmentStatusColor(
                            appointment.status
                          )}`}
                        >
                          {appointment.status}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No appointments today</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Report Generator Modal */}
        <DoctorReportGenerator
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          doctorData={loginUser}
          appointmentsData={allAppointments}
          statsData={dashboardStats}
        />
      </div>
    </div>
  );
};

export default DoctorDashboard;
